// Order updates (migration 055; Mitch, 2026-11-02): what a vendor says about an order of theirs, in the vendor portal or
// entered by HHL for them (vendors not on the portal): confirmed (their order number and ship dates), progress (units
// made or ready per line, a build's stage and units made), a new ship date with why, shipments with tracking (as many
// as it takes, to wherever the PO ships to), a note. Checked by the portal's routes (server/src/portal/orders.ts) and
// the staff mutations (shared/mutations-portal.ts); done by server/src/order-updates.ts.
import { z } from "zod";
import { MAKE_STAGES } from "./consignment.ts";

const text = (max = 200) => z.string().trim().max(max);
const day = z.iso.date("Use a date like 2026-12-31");
const qty = z.number().nonnegative("Quantities are 0 or more").max(10_000_000);

/** Confirmed: their order number, when it ships (all of it, or line by line), a note. */
export const confirmInput = z.object({
  vendor_ref: text(100).optional(),
  vendor_ship_date: day.nullable().optional(),
  lines: z
    .array(z.object({ po_line_id: z.uuid(), vendor_date: day.nullable() }))
    .max(500)
    .optional(),
  note: text(2000).optional(),
});

/** Progress: in production, units made or ready per line, and on a make order each build's stage and units made. */
export const progressInput = z.object({
  in_production: z.boolean().optional(),
  lines: z
    .array(z.object({ po_line_id: z.uuid(), qty_ready: qty }))
    .max(500)
    .optional(),
  builds: z
    .array(z.object({ make_order_id: z.uuid(), made: qty.positive("Quantities are more than 0").optional(), stage: z.enum(MAKE_STAGES).optional() }))
    .max(100)
    .optional(),
  note: text(2000).optional(),
});

/** It ships later (or sooner): the new date and why. */
export const newDateInput = z.object({ vendor_ship_date: day, note: text(2000).min(1, "Say why the date changed") });

/** A shipment: when, how (carrier and tracking), how many boxes, and what's in it. */
export const shipmentInput = z.object({
  shipped_on: day,
  carrier: text(50),
  tracking_number: text(100),
  boxes: z.number().int().positive().nullable().optional(),
  note: text(2000).optional(),
  lines: z
    .array(z.object({ po_line_id: z.uuid(), qty: qty.positive("Quantities are more than 0") }))
    .min(1, "Say what's in it")
    .max(500),
});

export const noteInput = z.object({ note: text(2000).min(1, "Write the note") });

/**
 * A shipment received where it went, by the CM it was sent to (bought from one vendor, sent to another): each line's
 * good and damaged units.
 */
export const receiveInput = z.object({
  lines: z
    .array(z.object({ po_line_id: z.uuid(), good: qty, damaged: qty }))
    .min(1, "Count what arrived")
    .max(500),
  note: text(2000).optional(),
});
export type ReceiveInput = z.infer<typeof receiveInput>;

/** How the timeline and forms read an order, the same for the portal and the staff app. */
export type OrderLineView = {
  id: string;
  sku: string;
  name: string;
  vendor_sku: string;
  qty: number;
  qty_shipped: number;
  qty_received: number;
  qty_ready: number;
  vendor_date: string | null;
  promised_date: string | null;
};
export type BuildView = { id: string; po_line_id: string; sku: string; name: string; qty: number; made: number; stage: string };
export type ShipmentView = {
  id: string;
  shipped_on: string;
  carrier: string;
  tracking_number: string;
  boxes: number | null;
  note: string;
  from_them: boolean;
  by_name: string;
  received_at: string | null;
  lines: { po_line_id: string; sku: string; name: string; qty: number }[];
};
export type UpdateView = {
  id: string;
  kind: "confirmed" | "progress" | "new_date" | "shipped" | "received" | "note";
  note: string;
  new_date: string | null;
  detail: Record<string, unknown>;
  from_them: boolean;
  by_name: string;
  created_at: string;
};

export type ConfirmInput = z.infer<typeof confirmInput>;
export type ProgressInput = z.infer<typeof progressInput>;
export type NewDateInput = z.infer<typeof newDateInput>;
export type ShipmentInput = z.infer<typeof shipmentInput>;
